import React, { useRef, useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { cn } from "@utils";
import { formatAmount } from '../../utils/Formatters';

const AutoResizeCurrencyInput = ({
  name,
  control,
  displayName,
  required,
  min,
  max,
  disabled,
  readOnly,
  placeholder = "0.00",
  onChange: onValueChange,
  errors,
  className,
  maxLength = 10
}) => {
  // Reference to a hidden span used to measure text width
  const measureRef = useRef(null);
  // State to track current input width
  const [inputWidth, setInputWidth] = useState(null);

  // Function to update width based on input value
  const updateWidth = (value) => {
    if (!measureRef.current) return;
    
    // Use placeholder if value is empty
    const textToMeasure = value || placeholder || "0";
    measureRef.current.textContent = textToMeasure;
    
    // Get the width + some padding
    const width = Math.max(measureRef.current.offsetWidth + 30, 50);
    setInputWidth(`${width}px`);
  };

  useEffect(() => {
    if (measureRef.current) {
      updateWidth("");
    }
  }, [placeholder]);

  const formatCurrencyInput = (value) => {
    if (!value) return value;
    
    if (value.startsWith('.')) {
      return `0${value}`;
    }
    
    return value;
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? `${displayName} is required` : null,
        min: min ? {
          value: min,
          message: `${displayName} must be greater than ${(formatAmount(min))}`
        } : null,
        max: max ? {
          value: max,
          message: `${displayName} must be less than ${formatAmount(max)}`
        } : null
      }}
      render={({ field: { ref, onChange, value, ...fieldProps } }) => (
        <div className="inline-flex items-center space-x-1 -mr-4">
          {/* Hidden span to measure text width */}
          <span 
            ref={measureRef} 
            className="absolute opacity-0 pointer-events-none text-5xl font-semibold"
            aria-hidden="true"
          ></span>
          
          <span className={cn(
              "text-5xl font-semibold text-black",
              value.length > 0 ? "text-black" : "text-zinc-300"
            )}>£</span>
          
          <input
            {...fieldProps}
            value={value}
            type="text"
            disabled={disabled}
            readOnly={readOnly}
            step="0.01"
            placeholder={placeholder}
            className={cn(
              "mx-none text-5xl font-semibold outline-none appearance-none transition-all duration-100 placeholder:text-zinc-300",
              "[&::-webkit-inner-spin-button]:appearance-none",
              "[&::-webkit-outer-spin-button]:appearance-none",
              "[&::-moz-number-spin-box]:appearance-none",
              // {
              //   "text-zinc-500": disabled,
              //   "text-red-900": !!errors,
              //   "text-black": !errors && !disabled,
              //   "placeholder:text-zinc-200": disabled,
              //   "placeholder:text-red-300": !!errors,
              //   "placeholder:text-zinc-400": !errors && !disabled,
              // },
              className
            )}
            style={{ width: inputWidth,}}
            onChange={e => {
              const newValue = e.target.value;

              if (newValue && newValue.length > maxLength) return;
              if (!/^\d*\.?\d{0,2}$/.test(newValue) || /[+\-]/.test(newValue)) return
              const formattedValue = formatCurrencyInput(newValue)
              if (formattedValue && formattedValue.length > maxLength) return;
              if (onValueChange) onValueChange(newValue);
              onChange(newValue);
              updateWidth(newValue);
            }}
            onFocus={() => updateWidth(value || placeholder)}
            onBlur={() => {
              if (!value) return;
              const formattedValue = formatCurrencyInput(value);
              if (formattedValue !== value) {
                onChange(formattedValue);
                if (onValueChange) {
                  onValueChange(formattedValue);
                }
              }
              updateWidth(formattedValue);
            }}
            ref={(e) => {
              ref(e);
              if (e) {
                // Use timeout to ensure DOM is fully rendered
                setTimeout(() => {
                  const formattedValue = formatCurrencyInput(value);
                  updateWidth(formattedValue || placeholder);
                }, 0);
              }
            }}
          />
        </div>
      )}
    />
  );
};

export default AutoResizeCurrencyInput;